import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { usePermission, useAuth } from "@/contexts/AuthContext";
import { fmtDateTime, apiErr } from "@/lib/format";
import { useCompany } from "@/contexts/CompanyContext";
import { toast } from "sonner";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import {
  ShieldCheckIcon, UsersIcon, ClipboardTextIcon, LockKeyIcon,
  CheckIcon, PlusIcon, TrashIcon, PencilSimpleIcon, ProhibitIcon,
  ArrowCounterClockwiseIcon, SignOutIcon, WarningIcon, KeyIcon, PowerIcon, AtIcon, ClockCounterClockwiseIcon,
  LightbulbIcon
} from "@phosphor-icons/react";

import { normalizar } from "@/components/LiveSearch";
import { PoliticaSesion } from "@/pages/UsuarioRegistro";

export default function Permissions() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(["roles", "users", "audit"].includes(params.get("tab")) ? params.get("tab") : "roles");
  return (
    <Layout title="Permisos" subtitle="Roles, accesos por módulo, bloqueo de usuarios y auditoría">
      <div className="flex gap-1 mb-5 border-b border-border/60" data-testid="perm-tabs">
        <T active={tab === "roles"} onClick={() => setTab("roles")} icon={ShieldCheckIcon} testid="tab-roles">Roles</T>
        <T active={tab === "users"} onClick={() => setTab("users")} icon={UsersIcon} testid="tab-users">Usuarios</T>
        <T active={tab === "audit"} onClick={() => setTab("audit")} icon={ClipboardTextIcon} testid="tab-audit">Auditoría</T>
      </div>
      {tab === "roles" && <RolesTab />}
      {tab === "users" && <UsersTab />}
      {tab === "audit" && <AuditTab />}
    </Layout>
  );
}

function T({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px transition ${active ? "border-agro-green text-agro-green" : "border-transparent text-text-muted hover:text-text-primary"}`}>
      <Icon size={14} /> {children}
    </button>
  );
}

/* ===================== ROLES ===================== */
function RolesTab() {
  const [meta, setMeta] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // rol en edición
  const canCreate = usePermission("permisos", "crear");
  const canEdit = usePermission("permisos", "modificar");
  const canDelete = usePermission("permisos", "eliminar");

  const load = async () => {
    setLoading(true);
    try {
      const [m, r] = await Promise.all([api.get("/permissions/modules"), api.get("/permissions/roles")]);
      setMeta(m.data); setRoles(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const newRol = () => setEditing({
    id: null, codigo: "", nombre: "", descripcion: "",
    permisos: Object.fromEntries(meta.modulos.map((m) => [m.key, []])),
    cuentas_bancarias_permitidas: [], ocultar_costos: false, ocultar_comisiones: false, ocultar_nomina: false,
    puede_sobrescribir_precios: false, puede_liberar_comisiones: false, ver_historial_acceso: false, puede_modificar_credito: false,
    restringir_fecha_operacion: false, credito_solo_solicitud: false, compras_solo_solicitud: false, sin_anulaciones: false,
    alcance_regional: false, aprueba_proformas: false, revisa_viaticos: false, autoriza_traslados: false,
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-text-secondary">{roles.length} roles configurados</div>
        {canCreate && <button onClick={newRol} data-testid="new-role-btn" className="bg-agro-green text-black font-medium rounded-md px-3 py-1.5 text-sm flex items-center gap-1.5 hover:bg-agro-green-hover active:scale-95 transition">
          <PlusIcon size={14} weight="bold" /> Nuevo rol
        </button>}
      </div>

      {loading ? <div className="text-center text-text-muted p-10">Cargando…</div>
      : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {roles.map((r) => (
            <div key={r.id} data-testid={`role-card-${r.codigo}`} className={`bg-card border rounded-lg p-4 ${r.es_sistema ? "border-agro-green/30" : "border-border/60"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-base font-semibold">{r.nombre}</span>
                    {r.es_sistema && <span className="text-[9px] uppercase bg-agro-green/10 text-agro-green border border-agro-green/30 px-1.5 py-0.5 rounded">sistema</span>}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted font-mono">{r.codigo}</div>
                </div>
                <span className="text-xs text-text-secondary"><UsersIcon size={12} className="inline mr-0.5" />{r.usuarios_count}</span>
              </div>
              <p className="text-xs text-text-secondary mt-2 line-clamp-2">{r.descripcion}</p>
              <div className="mt-3 flex flex-wrap gap-1">
                {Object.entries(r.permisos || {}).filter(([_, v]) => v.length > 0).slice(0, 5).map(([k, v]) => (
                  <span key={k} className="text-[9px] bg-app border border-border rounded px-1.5 py-0.5">
                    {meta?.modulos.find((m) => m.key === k)?.label || k} <span className="text-agro-green">({v.length})</span>
                  </span>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {r.puede_sobrescribir_precios && <Badge color="yellow" l="Sobrescribir precios" />}
                {r.puede_liberar_comisiones && <Badge color="yellow" l="Liberar GRATIVA" />}
                {r.ocultar_costos && <Badge color="muted" l="Sin costos" />}
                {r.restringir_fecha_operacion && <Badge color="muted" l="Solo fecha corriente" />}
                {r.credito_solo_solicitud && <Badge color="muted" l="Crédito: solo solicita" />}
                {r.ocultar_comisiones && <Badge color="muted" l="Sin GRATIVA" />}
                {r.ocultar_nomina && <Badge color="muted" l="Sin nómina" />}
              </div>
              <div className="mt-4 flex gap-2">
                {canEdit && <button onClick={() => setEditing({ ...r, permisos: { ...Object.fromEntries(meta.modulos.map((m) => [m.key, []])), ...r.permisos } })} data-testid={`edit-role-${r.codigo}`}
                  className="text-xs flex items-center gap-1 px-3 py-1 border border-border rounded hover:border-agro-green hover:text-agro-green">
                  <PencilSimpleIcon size={12} /> Editar
                </button>}
                {!r.es_sistema && canDelete && (
                  <button onClick={async () => { if (!window.confirm(`Eliminar/desactivar rol "${r.nombre}"?`)) return; try { const { data } = await api.delete(`/permissions/roles/${r.id}`); toast.success(data.disabled ? `Rol desactivado (${data.usuarios_afectados} usuarios)` : "Rol eliminado"); load(); } catch (e) { toast.error(apiErr(e)); } }}
                    className="text-xs flex items-center gap-1 px-3 py-1 border border-border rounded text-red-400 hover:border-red-500/40">
                    <TrashIcon size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>}

      {editing && <RoleEditor rol={editing} meta={meta} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

// Nombre visible de cada acción de la matriz (la clave interna no cambia)
const ACCION_LABEL = { ver: "CONSULTAR", crear: "CREAR", modificar: "EDITAR", eliminar: "ANULAR", imprimir: "EXPORTAR / IMPRIMIR" };
const EXCLUSIVAS_ADMIN = [
  ["IMPORTAR DESDE EXCEL", "Carga masiva de clientes, productos, proveedores, empleados, estados de cuenta y documentos históricos"],
  ["VALORACIÓN DEL INVENTARIO", "Valor del inventario en pantallas, reportes y exportaciones"],
  ["BODEGAS", "Consulta y administración de la sección Bodegas"],
];

function RoleEditor({ rol, meta, onClose, onSaved }) {
  const [form, setForm] = useState({ ...rol, cuentas_bancarias_permitidas: rol.cuentas_bancarias_permitidas || [] });
  const [saving, setSaving] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  useEffect(() => {
    api.get("/treasury/accounts").then(({ data }) => setBankAccounts(data || [])).catch(() => setBankAccounts([]));
  }, []);
  const toggleCuenta = (id) => {
    const cur = form.cuentas_bancarias_permitidas || [];
    setForm({ ...form, cuentas_bancarias_permitidas: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };

  const togglePerm = (mod, acc) => {
    const current = form.permisos[mod] || [];
    const next = current.includes(acc) ? current.filter((a) => a !== acc) : [...current, acc];
    setForm({ ...form, permisos: { ...form.permisos, [mod]: next } });
  };

  const toggleAll = (mod, on) => setForm({ ...form, permisos: { ...form.permisos, [mod]: on ? [...meta.acciones] : [] } });

  const save = async () => {
    if (!form.codigo || !form.nombre) return toast.error("Código y nombre son obligatorios");
    setSaving(true);
    try {
      // Se envía un rol limpio: módulos sin acciones = [], sin campos vacíos (null) ni datos de solo lectura
      const permisos = Object.fromEntries(meta.modulos.map((m) => [m.key, (form.permisos?.[m.key] || []).filter(Boolean)]));
      const body = Object.fromEntries(Object.entries({ ...form, permisos })
        .filter(([k, v]) => v !== null && v !== undefined && !["usuarios_count", "_id"].includes(k)));
      if (form.id) await api.put(`/permissions/roles/${form.id}`, body);
      else await api.post("/permissions/roles", { ...body, id: undefined });
      toast.success("Rol guardado");
      onSaved();
    } catch (e) { toast.error(`No se pudo guardar el rol: ${apiErr(e)}`, { duration: 10000 }); }
    finally { setSaving(false); }
  };
  const m = useModalForm({ value: form, onClose, onSave: save });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="role-editor">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex justify-between items-center z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">{form.id ? "Editar rol" : "Nuevo rol"}</div>
            {form.es_sistema && form.codigo !== "administrador" && (
              <div className="text-[10px] text-agro-green" data-testid="role-system-note">Rol de sistema: puedes autorizar o quitar funciones y guardar; solo no se elimina.</div>
            )}
            <h3 className="font-heading text-lg font-semibold">{form.nombre || "Sin nombre"}</h3>
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded text-text-secondary">Cancelar</button>
            <button onClick={m.guardar} disabled={saving || m.saving || !(form.codigo && form.nombre)} title="Código y nombre son obligatorios" data-testid="save-role-btn" className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40">{saving || m.saving ? "Guardando…" : "Guardar"}</button>
            <BotonCerrar onClick={m.cerrar} />
          </div>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Código">
              <input value={form.codigo} onChange={(e) => setForm({...form, codigo: e.target.value.toLowerCase().replace(/\s+/g, "_")})} disabled={!!form.id} className="w-full bg-app border border-border rounded px-3 py-2 text-sm disabled:opacity-50" />
            </Field>
            <Field label="Nombre">
              <input value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} className="w-full bg-app border border-border rounded px-3 py-2 text-sm" />
            </Field>
          </div>
          <Field label="Descripción">
            <input value={form.descripcion} onChange={(e) => setForm({...form, descripcion: e.target.value})} className="w-full bg-app border border-border rounded px-3 py-2 text-sm" />
          </Field>

          {/* Matriz de permisos */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Permisos por módulo</div>
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-sidebar">
                  <tr>
                    <th className="text-left p-2 pl-3">Módulo</th>
                    {meta.acciones.map((a) => <th key={a} className="p-2 text-center w-20 text-[10px] tracking-wider">{ACCION_LABEL[a] || a}</th>)}
                    <th className="p-2 text-center w-20">Todos</th>
                  </tr>
                </thead>
                <tbody>
                  {meta.modulos.map((m) => {
                    const has = form.permisos[m.key] || [];
                    const all = has.length === meta.acciones.length;
                    return (
                      <tr key={m.key} className="border-t border-border/30 hover:bg-white/[0.02]">
                        <td className="p-2 pl-3">{m.label}</td>
                        {meta.acciones.map((a) => {
                          const on = has.includes(a);
                          return (
                            <td key={a} className="text-center">
                              <button onClick={() => togglePerm(m.key, a)} data-testid={`perm-${m.key}-${a}`}
                                className={`w-6 h-6 rounded border ${on ? "bg-agro-green border-agro-green text-black" : "border-border text-text-muted hover:border-agro-green"}`}>
                                {on ? <CheckIcon size={12} weight="bold" /> : ""}
                              </button>
                            </td>
                          );
                        })}
                        <td className="text-center">
                          <button onClick={() => toggleAll(m.key, !all)}
                            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${all ? "bg-agro-green/10 text-agro-green border-agro-green/30" : "border-border text-text-muted hover:text-text-primary"}`}>
                            {all ? "Todo" : "Nada"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Permisos especiales */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Restricciones y permisos especiales</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <SwitchRow label="Sobrescribir precios" desc="Puede forzar precios diferentes a las listas P0–P4" v={form.puede_sobrescribir_precios} on={(v) => setForm({...form, puede_sobrescribir_precios: v})} />
              <SwitchRow label="Liberar GRATIVA" desc="Aprueba el pago de GRATIVA (Gratificación Variable) a los RTC" v={form.puede_liberar_comisiones} on={(v) => setForm({...form, puede_liberar_comisiones: v})} />
              <SwitchRow label="Cambiar condiciones de crédito" desc="Modifica límite, días de crédito y días para bloqueo del cliente (acción sensible; no aplica a roles con crédito solo por solicitud)" v={Boolean(form.puede_modificar_credito)} on={(v) => setForm({...form, puede_modificar_credito: v})} testid="perm-credito" />
              <SwitchRow label="Ver historial de acceso" desc="Consulta inicios de sesión, actividad y duración de los usuarios (el Administrador siempre puede)" v={Boolean(form.ver_historial_acceso)} on={(v) => setForm({...form, ver_historial_acceso: v})} testid="perm-historial-acceso" />
              <SwitchRow label="VISUALIZAR VALORES FINANCIEROS" desc="Costos, márgenes y utilidades en pantallas, reportes y exportaciones (la valoración del inventario sigue siendo exclusiva del Administrador General)" v={!form.ocultar_costos} on={(v) => setForm({...form, ocultar_costos: !v})} testid="perm-ver-valores" />
              <SwitchRow label="Ocultar GRATIVA" desc="Esconde la información de GRATIVA (Gratificación Variable) de los RTC" v={form.ocultar_comisiones} on={(v) => setForm({...form, ocultar_comisiones: v})} />
              <SwitchRow label="Ocultar nómina" desc="Esconde sueldos y datos sensibles de empleados" v={form.ocultar_nomina} on={(v) => setForm({...form, ocultar_nomina: v})} />
              <SwitchRow label="OPERAR CON FECHAS DIFERENTES AL DÍA EN CURSO" desc="Si está desactivado, solo registra cobros, facturas, notas, ajustes y demás operaciones con la fecha del día de la empresa (no aplica a vencimientos)" v={!form.restringir_fecha_operacion} on={(v) => setForm({...form, restringir_fecha_operacion: !v})} testid="perm-otra-fecha" />
              <SwitchRow label="Crédito: solo solicitar" desc="No modifica condiciones ni desbloquea; solicita al Administrador" v={form.credito_solo_solicitud} on={(v) => setForm({...form, credito_solo_solicitud: v})} />
              <SwitchRow label="Compras: solo solicitar" desc="No genera ni aprueba órdenes de compra; las solicita" v={form.compras_solo_solicitud} on={(v) => setForm({...form, compras_solo_solicitud: v})} />
              <SwitchRow label="Sin anulaciones" desc="No anula, elimina ni revierte documentos emitidos" v={form.sin_anulaciones} on={(v) => setForm({...form, sin_anulaciones: v})} />
              <SwitchRow label="Alcance por regiones" desc="Solo clientes de las regiones asignadas a cada usuario (se asignan en Usuarios)" v={form.alcance_regional} on={(v) => setForm({...form, alcance_regional: v})} />
              <SwitchRow label="Aprueba proformas" desc="Revisa las proformas enviadas por los RTC" v={form.aprueba_proformas} on={(v) => setForm({...form, aprueba_proformas: v})} />
              <SwitchRow label="Revisa viáticos" desc="Aprueba, rechaza o devuelve viáticos (nunca los propios)" v={form.revisa_viaticos} on={(v) => setForm({...form, revisa_viaticos: v})} />
              <SwitchRow label="Autoriza traslados" desc="Aprueba solicitudes de traslado de inventario (no las ejecuta)" v={form.autoriza_traslados} on={(v) => setForm({...form, autoriza_traslados: v})} />
            </div>
          </div>

          <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3" data-testid="exclusivas-admin">
            <div className="text-[10px] tracking-wider text-red-300 font-semibold mb-2">EXCLUSIVO DEL ADMINISTRADOR GENERAL · NO ASIGNABLE A OTROS ROLES</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {EXCLUSIVAS_ADMIN.map(([t, d]) => (
                <div key={t} className="text-xs"><div className="font-semibold flex items-center gap-1"><LockKeyIcon size={12} aria-hidden="true" /> {t}</div><div className="text-text-muted">{d}</div></div>
              ))}
            </div>
          </div>

          <div className="bg-agro-yellow/5 border border-agro-yellow/30 rounded-md p-3 text-xs">
            <strong className="text-agro-yellow flex items-center gap-1 mb-1"><WarningIcon size={12} /> Cuentas bancarias permitidas</strong>
            <p className="text-text-secondary mb-2">Si no seleccionas ninguna, el rol verá TODAS las cuentas de tesorería. Si seleccionas algunas, solo podrá ver y operar (movimientos, transferencias, depósitos) con esas. Aplica a roles con acceso a Contabilidad o Cobranza.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1" data-testid="bank-accounts-selector">
              {bankAccounts.length === 0 && <div className="text-text-muted">No hay cuentas de tesorería registradas.</div>}
              {bankAccounts.map((a) => {
                const on = (form.cuentas_bancarias_permitidas || []).includes(a.id);
                return (
                  <button key={a.id} type="button" onClick={() => toggleCuenta(a.id)} data-testid={`bank-acc-${a.codigo}`}
                    className={`text-left px-2 py-1.5 rounded border flex items-center justify-between ${on ? "bg-agro-green/10 border-agro-green/40 text-agro-green" : "bg-app/40 border-border text-text-secondary"}`}>
                    <span><span className="font-mono text-[10px] mr-1">{a.codigo}</span>{a.nombre}{a.banco ? ` · ${a.banco}` : ""}</span>
                    {on && <CheckIcon size={12} weight="bold" />}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-text-muted mt-1">
              {(form.cuentas_bancarias_permitidas || []).length === 0 ? "Todas las cuentas permitidas" : `${form.cuentas_bancarias_permitidas.length} cuenta(s) seleccionada(s)`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div>
    <label className="text-[9px] uppercase tracking-wider text-text-muted mb-1 block">{label}</label>
    {children}
  </div>;
}

function SwitchRow({ label, desc, v, on, testid }) {
  return (
    <button onClick={() => on(!v)} data-testid={testid} className={`text-left p-3 rounded border transition ${v ? "bg-agro-green/10 border-agro-green/40" : "bg-app/40 border-border"}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className={`text-xs font-medium ${v ? "text-agro-green" : "text-text-primary"}`}>{label}</div>
          <div className="text-[10px] text-text-muted mt-0.5">{desc}</div>
        </div>
        <div className={`w-9 h-5 rounded-full transition relative ${v ? "bg-agro-green" : "bg-border"}`}>
          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-card transition-all ${v ? "left-4" : "left-0.5"}`} />
        </div>
      </div>
    </button>
  );
}

function Badge({ color, l }) {
  const m = { yellow: "text-agro-yellow bg-agro-yellow/10 border-agro-yellow/30", muted: "text-text-muted bg-app border-border" }[color];
  return <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${m}`}>{l}</span>;
}

/* ===================== USUARIOS ===================== */
function UsersTab() {
  const navigate = useNavigate();
  const { user: yo } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRoles, setEditingRoles] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/permissions/users"), api.get("/permissions/roles")]);
      setUsers(u.data); setRoles(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const block = async (u) => {
    const motivo = window.prompt(`Motivo del bloqueo de ${u.email}: (ej: dispositivo perdido, anomalía detectada)`);
    if (!motivo) return;
    try {
      await api.post(`/permissions/users/${u.id}/block`, { motivo, cerrar_sesiones: true });
      toast.success(`${u.email} bloqueado. Sesiones cerradas.`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const unblock = async (u) => {
    const motivo = window.prompt(`Motivo para reactivar a ${u.email}:`);
    if (motivo === null) return;
    if (!motivo.trim()) { toast.error("El motivo es obligatorio"); return; }
    try { await api.post(`/permissions/users/${u.id}/unblock`, { motivo }); toast.success("Usuario reactivado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const deactivate = async (u) => {
    if (!window.confirm(`¿Desactivar a ${u.email}? No podrá iniciar sesión hasta que se reactive.`)) return;
    try { await api.post(`/permissions/users/${u.id}/deactivate`); toast.success("Usuario desactivado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const eliminar = async (u) => {
    if (!window.confirm(`¿Eliminar definitivamente la cuenta ${u.email}? No podrá iniciar sesión y la acción no se puede deshacer. El historial de sus operaciones se conserva.`)) return;
    try { await api.delete(`/permissions/users/${u.id}`); toast.success("Usuario eliminado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  // Cambiar el correo / nombre de usuario con que inicia sesión (p. ej. de «dmaldonado» a su correo real)
  const cambiarAcceso = async (u) => {
    const nuevo = window.prompt(`Correo o nombre de usuario para ${u.nombre || u.email}.
Actual: ${u.email}
(La contraseña y los roles no cambian)`, u.email);
    if (!nuevo || nuevo.trim().toLowerCase() === u.email) return;
    try { await api.post(`/permissions/users/${u.id}/email`, { email: nuevo.trim() }); toast.success(`Ahora inicia sesión con ${nuevo.trim().toLowerCase()}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  // Acceso a GRATIVA del usuario (menú, permisos y datos); no cambia sus regiones
  const cambiarGrativa = async (u) => {
    const activar = !u.grativa_efectiva;
    if (!window.confirm(`${activar ? "Activar" : "Desactivar"} el acceso a GRATIVA de ${u.nombre || u.email}?`)) return;
    try { await api.post(`/permissions/users/${u.id}/grativa`, { activo: activar }); toast.success(`GRATIVA ${activar ? "activada" : "desactivada"}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const logout = async (u) => {
    if (!window.confirm(`Cerrar sesiones activas de ${u.email} en todos los dispositivos?`)) return;
    try { await api.post(`/permissions/users/${u.id}/logout-all`); toast.success("Sesiones cerradas"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      {yo?.effective?.is_admin && <PoliticaSesion />}
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-text-secondary">{users.length} usuarios</div>
        <button
          onClick={() => setCreating(true)}
          data-testid="new-user-btn"
          className="bg-agro-green text-black font-medium rounded-md px-3 py-1.5 text-sm flex items-center gap-1.5 hover:bg-agro-green-hover active:scale-95 transition"
        >
          <PlusIcon size={14} weight="bold" /> Nuevo usuario
        </button>
      </div>
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">Usuario</th>
              <th className="p-3">Roles</th>
              <th className="p-3">Estado</th>
              <th className="p-3">Bloqueo / Sesiones</th>
              <th className="p-3 pr-5 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="5" className="p-8 text-center text-text-muted">Cargando…</td></tr>
            : users.map((u) => (
              <tr key={u.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`user-row-${u.email}`}>
                <td className="p-3 pl-5">
                  <button type="button" onClick={() => navigate(`/permisos/usuarios/${u.id}`)} className="text-sm font-medium hover:text-agro-green text-left" data-testid={`user-open-${u.email}`}>{u.nombre || u.email}</button>
                  <div className="text-[10px] text-text-muted">{u.email} · {u.role}</div>
                  {u.empleado ? (
                    <div className="text-[10px] text-agro-green mt-0.5 flex items-center gap-1">
                      <UsersIcon size={9} weight="fill" />
                      <span className="font-mono">{u.empleado.codigo}</span>
                      <span>· {u.empleado.puesto || "—"}</span>
                    </div>
                  ) : (
                    <div className="text-[10px] text-agro-yellow/80 mt-0.5 flex items-center gap-1"><WarningIcon size={10} aria-hidden="true" /> Sin empleado vinculado</div>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {(u.roles_detail || []).map((r) => (
                      <span key={r.id} className="text-[10px] bg-app border border-border rounded px-1.5 py-0.5">{r.nombre}</span>
                    ))}
                    {(!u.roles_detail || u.roles_detail.length === 0) && <span className="text-[10px] text-text-muted">— sin rol</span>}
                  </div>
                  {u.grativa_aplica && (
                    <button type="button" onClick={() => cambiarGrativa(u)} data-testid={`grativa-toggle-${u.email}`}
                      title="Acceso a GRATIVA (menú, permisos y datos). No amplía sus regiones."
                      className={`mt-1 text-[10px] px-1.5 py-0.5 rounded border ${u.grativa_efectiva ? "bg-agro-green/10 border-agro-green/40 text-agro-green" : "border-border text-text-muted"}`}>
                      Acceso a GRATIVA: {u.grativa_efectiva ? "activado" : "desactivado"}
                    </button>
                  )}
                </td>
                <td className="p-3">
                  <EstadoBadge estado={u.estado} />
                </td>
                <td className="p-3 text-xs">
                  {u.estado === "bloqueado" && (
                    <div>
                      <div className="text-red-400 font-medium">{u.bloqueo_motivo}</div>
                      <div className="text-[10px] text-text-muted">por {u.bloqueado_por} · {fmtDateTime(u.bloqueado_at)}</div>
                    </div>
                  )}
                  {u.estado === "inactivo" && (
                    <div className="text-[10px] text-text-muted">Desactivado por {u.desactivado_por || "—"} · {fmtDateTime(u.desactivado_at)}</div>
                  )}
                  {u.estado === "activo" && u.reactivacion_motivo && (
                    <div className="text-[10px] text-agro-green">Reactivado: {u.reactivacion_motivo} · {u.reactivado_por}</div>
                  )}
                  {u.estado !== "bloqueado" && u.session_invalidate_after_ts && (
                    <div className="text-[10px] text-text-muted">Sesiones cerradas: {new Date(u.session_invalidate_after_ts * 1000).toLocaleString()}</div>
                  )}
                </td>
                <td className="p-3 pr-5 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => setEditingRoles({ user: u, selected: u.roles || [] })} data-testid={`edit-roles-${u.email}`}
                      className="p-1.5 text-text-muted hover:text-agro-green" title="Asignar roles"><KeyIcon size={14} /></button>
                    <button onClick={() => navigate(`/permisos/usuarios/${u.id}`)} data-testid={`access-history-${u.email}`} className="p-1.5 text-text-muted hover:text-agro-green" title="Historial de acceso" aria-label={`Historial de acceso de ${u.nombre || u.email}`}><ClockCounterClockwiseIcon size={14} /></button>
                    <button onClick={() => cambiarAcceso(u)} data-testid={`change-email-${u.email}`} className="p-1.5 text-text-muted hover:text-agro-green" title="Cambiar correo / usuario"><AtIcon size={14} /></button>
                    <button onClick={() => logout(u)} className="p-1.5 text-text-muted hover:text-agro-yellow" title="Cerrar sesiones remotas" aria-label="Cerrar sesiones remotas"><SignOutIcon size={14} /></button>
                    {u.estado === "bloqueado" || u.estado === "inactivo"
                      ? <button onClick={() => unblock(u)} data-testid={`unblock-${u.email}`} className="p-1.5 text-agro-green hover:bg-agro-green/10 rounded" title="Reactivar"><ArrowCounterClockwiseIcon size={14} weight="bold" /></button>
                      : <>
                          <button onClick={() => block(u)} data-testid={`block-${u.email}`} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded" title="Bloquear"><ProhibitIcon size={14} weight="bold" /></button>
                          <button onClick={() => deactivate(u)} data-testid={`deactivate-${u.email}`} className="p-1.5 text-text-muted hover:text-red-400 rounded" title="Desactivar"><PowerIcon size={14} weight="bold" /></button>
                        </>}
                    <button onClick={() => eliminar(u)} data-testid={`delete-user-${u.email}`} className="p-1.5 text-text-muted hover:text-red-400 rounded" title="Eliminar usuario"><TrashIcon size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingRoles && (
        <AssignRolesModal data={editingRoles} roles={roles} onClose={() => setEditingRoles(null)} onSaved={() => { setEditingRoles(null); load(); }} />
      )}
      </div>

      {creating && (
        <CreateUserModal
          roles={roles}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateUserModal({ roles, onClose, onCreated }) {
  const [empleados, setEmpleados] = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [grativa, setGrativa] = useState(null);           // null = según el rol (RTC: desactivado)
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    api.get("/permissions/employees-available")
      .then(({ data }) => setEmpleados(data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoadingEmps(false));
  }, []);

  const palabrasEmp = normalizar(search).split(/\s+/).filter(Boolean);
  const filteredEmps = empleados.filter((e) => {
    const t = normalizar(`${e.nombre || ""} ${e.codigo || ""} ${e.identidad || ""} ${e.puesto || ""}`);
    return palabrasEmp.every((w) => t.includes(w));
  });

  const selectEmpleado = async (emp) => {
    setSelectedEmp(emp);
    // Correo personal o, si no tiene, el institucional (se puede cambiar por un nombre de usuario)
    const sugerido = emp.email || emp.email_institucional;
    if (sugerido && !email) setEmail(sugerido);
    // Integración Empleados↔Permisos: solo SUGIERE un rol según el cargo (el cargo no concede permisos;
    // los roles se eligen de forma explícita)
    try {
      const { data } = await api.get(`/permissions/suggest-role?empleado_id=${emp.id}`);
      if (data?.rol) {
        setSuggestion(data);
      } else {
        setSuggestion(null);
      }
    } catch { setSuggestion(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedEmp) {
      toast.error("Selecciona el empleado al que pertenece este usuario");
      return;
    }
    if (!email || !password) {
      toast.error("Completa email y contraseña");
      return;
    }
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (password !== password2) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setSaving(true);
    try {
      await api.post("/permissions/users", {
        email: email.trim().toLowerCase(),
        nombre: selectedEmp.nombre,
        password,
        empleado_id: selectedEmp.id,
        roles: selectedRoles,
        companies: [],
        grativa_acceso: grativa,
      });
      toast.success(`Usuario para ${selectedEmp.nombre} creado`);
      onCreated();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (rid) => {
    setSelectedRoles((cur) => (cur.includes(rid) ? cur.filter((x) => x !== rid) : [...cur, rid]));
  };
  const m = useModalForm({ value: { e: selectedEmp?.id || "", email, password, password2, selectedRoles }, onClose, onSave: submit });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div
        className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="create-user-modal"
      >
        <form onSubmit={m.guardar}>
          <div className="p-5 border-b border-border flex items-start justify-between gap-3">
            <div>
              <h3 className="font-heading text-xl flex items-center gap-2"><UsersIcon size={20} className="text-agro-green" /> Nuevo usuario</h3>
              <p className="text-xs text-text-muted mt-1">Cada cuenta debe estar vinculada a un empleado del módulo de RRHH.</p>
            </div>
            <BotonCerrar onClick={m.cerrar} />
          </div>

          <div className="p-5 space-y-5">
            {/* 1) Seleccionar empleado */}
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-2 font-medium">
                1. Empleado vinculado <span className="text-red-400 normal-case">*</span>
              </label>

              {selectedEmp ? (
                <div className="bg-agro-green/10 border border-agro-green/30 rounded-md p-3 flex items-center justify-between" data-testid="selected-emp">
                  <div>
                    <div className="font-medium text-sm flex items-center gap-2">
                      {selectedEmp.nombre}
                      <span className="text-[10px] font-mono text-text-muted bg-app px-1.5 py-0.5 rounded">{selectedEmp.codigo}</span>
                    </div>
                    <div className="text-[11px] text-text-muted">
                      {selectedEmp.puesto || "—"} · {selectedEmp.departamento || "Sin depto"}
                      {selectedEmp.identidad && <> · Identidad: {selectedEmp.identidad}</>}
                    </div>
                  </div>
                  <button type="button" onClick={() => { setSelectedEmp(null); setEmail(""); }} className="text-xs text-text-muted hover:text-red-400" data-testid="clear-emp">
                    Cambiar
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre, código, identidad o puesto…"
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none mb-2"
                    data-testid="emp-search"
                  />
                  <div className="border border-border/40 rounded-md bg-app/40 max-h-56 overflow-y-auto">
                    {loadingEmps ? (
                      <div className="p-4 text-center text-text-muted text-xs">Cargando empleados…</div>
                    ) : filteredEmps.length === 0 ? (
                      <div className="p-4 text-center text-text-muted text-xs">
                        {empleados.length === 0
                          ? "No hay empleados disponibles. Crea primero el empleado en RRHH → Empleados."
                          : "Sin resultados para tu búsqueda."}
                      </div>
                    ) : (
                      filteredEmps.map((emp) => (
                        <button
                          key={emp.id}
                          type="button"
                          onClick={() => selectEmpleado(emp)}
                          data-testid={`emp-pick-${emp.codigo}`}
                          className="w-full text-left p-2.5 border-b border-border/30 last:border-b-0 hover:bg-agro-green/5 transition flex items-center justify-between"
                        >
                          <div>
                            <div className="text-sm font-medium flex items-center gap-2">
                              {emp.nombre}
                              <span className="text-[10px] font-mono text-text-muted bg-card px-1.5 py-0.5 rounded">{emp.codigo}</span>
                            </div>
                            <div className="text-[11px] text-text-muted">{emp.puesto || "—"} · {emp.departamento || "Sin depto"}</div>
                          </div>
                          <PlusIcon size={12} className="text-text-muted" />
                        </button>
                      ))
                    )}
                  </div>
                  <div className="text-[10px] text-text-muted mt-1.5">
                    Solo se muestran empleados activos sin cuenta. ¿No aparece? Créalo primero en <span className="text-agro-green">RRHH → Empleados</span>.
                  </div>
                </>
              )}
            </div>

            {/* 2) Email + password (solo si hay empleado seleccionado) */}
            {selectedEmp && (
              <>
                <FieldRow label="2. Correo o nombre de usuario (para iniciar sesión)" required>
                  <input
                    type="text"
                    autoCapitalize="none"
                    spellCheck={false}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="juan@agroport.hn  o solo  jperez"
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none lowercase"
                    data-testid="create-user-email"
                  />
                  <p className="text-[10px] text-text-muted mt-1">Puedes poner solo el nombre de usuario (letras, números, punto o guion) y cambiarlo después por su correo con el botón @ de la lista.</p>
                </FieldRow>

                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="3. Contraseña" required>
                    <div className="relative">
                      <input
                        type={showPwd ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="mín. 6 caracteres"
                        className="w-full bg-app border border-border rounded-md px-3 py-2 pr-12 text-sm focus:border-agro-green outline-none"
                        data-testid="create-user-pwd"
                      />
                      <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-agro-green text-[10px]">
                        {showPwd ? "Ocultar" : "Ver"}
                      </button>
                    </div>
                  </FieldRow>
                  <FieldRow label="Confirmar contraseña" required>
                    <input
                      type={showPwd ? "text" : "password"}
                      value={password2}
                      onChange={(e) => setPassword2(e.target.value)}
                      placeholder="repite la contraseña"
                      className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none"
                      data-testid="create-user-pwd2"
                    />
                  </FieldRow>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2 font-medium">
                    4. Roles iniciales <span className="text-text-muted/60 normal-case">(opcional, puedes asignar después)</span>
                  </div>
                  {suggestion?.rol && (
                    <div className="mb-2 text-[11px] bg-agro-yellow/10 border border-agro-yellow/30 text-agro-yellow rounded-md px-3 py-2" data-testid="role-suggestion">
                      <LightbulbIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> Rol sugerido: <strong>{suggestion.rol.nombre}</strong> ({suggestion.fuente}). El cargo no asigna permisos: márcalo abajo solo si corresponde.
                      {!selectedRoles.includes(suggestion.rol.id) && (
                        <button type="button" className="ml-2 underline" data-testid="role-suggestion-apply"
                          onClick={() => setSelectedRoles((cur) => [...cur, suggestion.rol.id])}>Aplicar</button>
                      )}
                    </div>
                  )}
                  <div className="space-y-1 max-h-56 overflow-y-auto pr-1 border border-border/40 rounded-md p-2 bg-app/40">
                    {roles.length === 0 && <div className="text-xs text-text-muted p-2">Aún no hay roles. Créalos en la pestaña Roles.</div>}
                    {roles.map((r) => {
                      const on = selectedRoles.includes(r.id);
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => toggleRole(r.id)}
                          data-testid={`role-toggle-${r.codigo}`}
                          className={`w-full text-left p-2 rounded border transition flex items-center justify-between text-sm ${on ? "bg-agro-green/10 border-agro-green/30" : "bg-app/40 border-border hover:border-agro-green/30"}`}
                        >
                          <div>
                            <div className={`font-medium ${on ? "text-agro-green" : ""}`}>{r.nombre}</div>
                            <div className="text-[10px] text-text-muted">{r.descripcion}</div>
                          </div>
                          {on && <CheckIcon size={14} className="text-agro-green" weight="bold" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {(() => {
                  const sel = roles.filter((r) => selectedRoles.includes(r.id));
                  const conGrativa = sel.some((r) => (r.permisos?.comisiones || []).length > 0);
                  if (!conGrativa) return null;
                  const porDefecto = !sel.some((r) => r.alcance_regional);
                  const valor = grativa === null ? porDefecto : grativa;
                  return (
                    <label className="flex items-center justify-between gap-3 bg-app/40 border border-border rounded-md p-3 text-sm" data-testid="create-user-grativa">
                      <span>Acceso a GRATIVA: <b className={valor ? "text-agro-green" : "text-text-muted"}>{valor ? "activado" : "desactivado"}</b>
                        <span className="block text-[10px] text-text-muted">{porDefecto ? "" : "Para RTC queda desactivado por defecto. "}Puedes cambiarlo después desde la lista de usuarios; no amplía sus regiones.</span></span>
                      <input type="checkbox" checked={valor} onChange={(e) => setGrativa(e.target.checked)} className="w-4 h-4 accent-agro-green" data-testid="create-user-grativa-check" />
                    </label>
                  );
                })()}

                <div className="bg-agro-yellow/5 border border-agro-yellow/20 rounded-md p-3 text-[11px] text-agro-yellow/90 flex gap-2">
                  <WarningIcon size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
                  <div>
                    Anota la contraseña antes de guardar — no se mostrará después. Comunícala al usuario por un medio seguro y pídele que la cambie en su primer ingreso.
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="p-4 border-t border-border flex flex-wrap items-center justify-end gap-2 bg-app/40">
            {/* Por qué «Crear usuario» está deshabilitado: se dice qué falta en lugar de solo atenuar el botón */}
            {(() => {
              const faltan = [
                !selectedEmp && "elegir el empleado",
                !email.trim() && "escribir el correo o nombre de usuario",
                password.length < 6 && "contraseña de al menos 6 caracteres",
                password.length >= 6 && password !== password2 && "repetir la misma contraseña en «Confirmar»",
              ].filter(Boolean);
              return faltan.length > 0 && (
                <p className="mr-auto text-xs text-agro-yellow flex items-center gap-1.5" role="status" data-testid="create-user-faltan">
                  <WarningIcon size={14} aria-hidden="true" /> Para crear el usuario falta: {faltan.join(" · ")}.
                </p>
              );
            })()}
            <button type="button" onClick={m.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary hover:bg-white/5" data-testid="create-user-cancel">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || m.saving || !selectedEmp || !email || password.length < 6 || password !== password2}
              className="px-4 py-2 text-sm bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              data-testid="create-user-submit"
            >
              {saving ? "Creando…" : (<><PlusIcon size={14} weight="bold" /> Crear usuario</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FieldRow({ label, required, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1.5 font-medium">
        {label} {required && <span className="text-red-400 normal-case">*</span>}
      </label>
      {children}
    </div>
  );
}

function EstadoBadge({ estado }) {
  const m = {
    activo: "bg-agro-green/10 text-agro-green border-agro-green/30",
    bloqueado: "bg-red-500/10 text-red-400 border-red-500/30",
    inactivo: "bg-text-muted/20 text-text-muted border-border",
  }[estado] || "border-border";
  return <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${m}`}>{estado}</span>;
}

function AssignRolesModal({ data, roles, onClose, onSaved }) {
  const [selected, setSelected] = useState(data.selected);
  const { branches = [] } = useCompany() || {};
  const [sucs, setSucs] = useState(data.user.sucursales || []);
  const [def, setDef] = useState(data.user.sucursal_id || "");
  // Alcance del RTC: regiones (y ubicaciones) de ESTE usuario; las asigna el Administrador General
  const rolRtc = roles.find((r) => r.codigo === "ventas_rtc");
  const conRtc = Boolean(rolRtc && selected.includes(rolRtc.id));
  const [alcance, setAlcance] = useState(null);
  const [regionesCat, setRegionesCat] = useState([]);
  const [bodegasCat, setBodegasCat] = useState([]);
  const [motivo, setMotivo] = useState("");
  useEffect(() => {
    api.get(`/permissions/users/${data.user.id}/alcance`).then(({ data: a }) => setAlcance(a)).catch(() => setAlcance({ regiones: [], bodegas: [], historial: [] }));
    api.get("/regions").then(({ data: r }) => setRegionesCat((r || []).filter((x) => x.activo !== false))).catch(() => {});
    api.get("/warehouses").then(({ data: w }) => setBodegasCat((w || []).filter((x) => x.activo !== false))).catch(() => {});
  }, [data.user.id]);
  const toggle = (k, v) => setAlcance((a) => ({ ...a, [k]: (a[k] || []).includes(v) ? a[k].filter((x) => x !== v) : [...(a[k] || []), v] }));
  const save = async () => {
    if (conRtc && alcance && !alcance.desde_rrhh && !(alcance.regiones || []).length
      && !window.confirm("El usuario RTC no tiene regiones asignadas: no verá información de clientes. ¿Guardar así?")) return;
    try {
      await api.post(`/permissions/users/${data.user.id}/assign-roles`, { roles: selected });
      await api.put(`/permissions/users/${data.user.id}/sucursales`, { sucursales: sucs, sucursal_id: def });
      if (alcance && (conRtc || (alcance.regiones || []).length)) {
        await api.put(`/permissions/users/${data.user.id}/alcance`, { regiones: alcance.regiones || [], bodegas: alcance.bodegas || [], motivo });
      }
      toast.success("Roles, sucursales y alcance actualizados"); onSaved();
    }
    catch (e) { toast.error(apiErr(e)); }
  };
  const m = useModalForm({ value: { selected, sucs, def, r: alcance?.regiones, b: alcance?.bodegas, motivo },
                           listo: alcance !== null, onClose, onSave: save });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-md max-h-[92vh] overflow-y-auto" data-testid="assign-roles-modal">
        <div className="p-4 border-b border-border flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg">Roles y sucursales de {data.user.email}</h3>
            <p className="text-[10px] text-text-muted">Multi-rol: selecciona uno o más</p>
          </div>
          <BotonCerrar onClick={m.cerrar} />
        </div>
        <div className="p-4 space-y-1 max-h-96 overflow-y-auto">
          {roles.map((r) => {
            const on = selected.includes(r.id);
            return (
              <button key={r.id} onClick={() => setSelected(on ? selected.filter((x) => x !== r.id) : [...selected, r.id])}
                className={`w-full text-left p-3 rounded border transition flex items-center justify-between ${on ? "bg-agro-green/10 border-agro-green/30" : "bg-app/40 border-border hover:border-agro-green/40"}`}>
                <div>
                  <div className={`text-sm font-medium ${on ? "text-agro-green" : ""}`}>{r.nombre}</div>
                  <div className="text-[10px] text-text-muted">{r.descripcion}</div>
                </div>
                {on && <CheckIcon size={16} className="text-agro-green" weight="bold" />}
              </button>
            );
          })}
        </div>
        <div className="p-4 border-t border-border space-y-2" data-testid="user-branches">
          <div className="text-[10px] uppercase tracking-wider text-text-muted">Sucursales con acceso (ninguna marcada = todas)</div>
          <div className="flex flex-wrap gap-2">
            {branches.map((b) => {
              const on = sucs.includes(b.id);
              return (
                <button key={b.id} type="button" data-testid={`user-branch-${b.codigo}`}
                  onClick={() => { const n = on ? sucs.filter((x) => x !== b.id) : [...sucs, b.id]; setSucs(n); if (on && def === b.id) setDef(""); }}
                  className={`px-2.5 py-1 rounded border text-xs ${on ? "bg-agro-green/10 border-agro-green/40 text-agro-green" : "border-border text-text-secondary"}`}>
                  {b.nombre}
                </button>
              );
            })}
          </div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted pt-1">Sucursal predeterminada (emite con ella si no elige otra)</label>
          <select value={def} onChange={(e) => setDef(e.target.value)} data-testid="user-branch-default"
                  className="w-full bg-app border border-border rounded px-2 py-1.5 text-sm">
            <option value="">— Sin predeterminada —</option>
            {branches.filter((b) => !sucs.length || sucs.includes(b.id)).map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </div>
        {conRtc && alcance && (
          <div className="p-4 border-t border-border space-y-2" data-testid="user-rtc-scope">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Regiones que atiende (RTC) *</div>
            {alcance.desde_rrhh ? (
              <div className="text-[11px] text-agro-green" data-testid="rtc-scope-rrhh">
                Se asignan en Recursos Humanos, en el expediente de {alcance.desde_rrhh.codigo} · {alcance.desde_rrhh.nombre}.
                Regiones vigentes: <b>{(alcance.regiones || []).join(", ") || "ninguna"}</b>.
              </div>
            ) : (
              <p className="text-[10px] text-text-muted">Solo verá los clientes de estas regiones (no los asignados a otro RTC). Sin regiones no ve clientes.
                Si el usuario corresponde a un RTC de Recursos Humanos, vincúlalo en su expediente y las regiones se asignarán desde ahí.</p>
            )}
            {!alcance.desde_rrhh && <div className="flex flex-wrap gap-2">
              {regionesCat.map((r) => {
                const on = (alcance.regiones || []).includes(r.codigo);
                return (
                  <button key={r.id || r.codigo} type="button" data-testid={`rtc-region-${r.codigo}`} onClick={() => toggle("regiones", r.codigo)}
                    className={`px-2.5 py-1 rounded border text-xs ${on ? "bg-agro-green/10 border-agro-green/40 text-agro-green" : "border-border text-text-secondary"}`}>
                    {r.nombre || r.codigo}
                  </button>
                );
              })}
            </div>}
            <div className="text-[10px] uppercase tracking-wider text-text-muted pt-1">Ubicaciones de inventario habilitadas (ninguna = todas)</div>
            <div className="flex flex-wrap gap-2">
              {bodegasCat.map((w) => {
                const on = (alcance.bodegas || []).includes(w.id);
                return (
                  <button key={w.id} type="button" onClick={() => toggle("bodegas", w.id)}
                    className={`px-2.5 py-1 rounded border text-xs ${on ? "bg-agro-green/10 border-agro-green/40 text-agro-green" : "border-border text-text-secondary"}`}>
                    {w.codigo} · {w.nombre}
                  </button>
                );
              })}
            </div>
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo del cambio (opcional)"
              className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs" data-testid="rtc-scope-motivo" />
            {(alcance.historial || []).length > 0 && (
              <details className="text-[10px] text-text-muted">
                <summary className="cursor-pointer">Historial de asignaciones ({alcance.historial.length})</summary>
                <ul className="mt-1 space-y-0.5" data-testid="rtc-scope-history">
                  {alcance.historial.slice(0, 10).map((h) => (
                    <li key={h.id}>{fmtDateTime(h.fecha)} · {h.por}: {(h.antes?.regiones || []).join(", ") || "—"} → <span className="text-text-primary">{(h.despues?.regiones || []).join(", ") || "sin regiones"}</span>{h.motivo ? ` (${h.motivo})` : ""}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded text-text-secondary">Cancelar</button>
          <button onClick={m.guardar} disabled={m.saving} data-testid="assign-roles-save" className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40">{m.saving ? "Guardando…" : "Guardar"}</button>
        </div>
      </div>
    </div>
  );
}

/* ===================== AUDITORÍA ===================== */
function AuditTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/permissions/audit").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-border/60">
        <h3 className="font-heading text-base">Auditoría de roles y usuarios</h3>
        <p className="text-[10px] text-text-muted">Registro de quién creó / editó / bloqueó / asignó roles</p>
      </div>
      <div className="overflow-x-auto max-h-[600px]">
        <table className="w-full text-xs">
          <thead className="bg-sidebar/60 sticky top-0">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-2 pl-4">Cuándo</th><th className="p-2">Acción</th>
              <th className="p-2">Recurso</th><th className="p-2">ID</th><th className="p-2">Por</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="5" className="p-6 text-center text-text-muted">Cargando…</td></tr>
            : items.length === 0 ? <tr><td colSpan="5" className="p-8 text-center text-text-muted">Sin eventos de auditoría</td></tr>
            : items.map((it, i) => (
              <tr key={i} className="border-t border-border/30">
                <td className="p-2 pl-4 text-text-secondary">{fmtDateTime(it.timestamp)}</td>
                <td className="p-2"><span className={`text-[10px] uppercase px-1.5 py-0.5 rounded border ${actionColor(it.action)}`}>{it.action}</span></td>
                <td className="p-2">{it.resource}</td>
                <td className="p-2 font-mono text-[10px]">{(it.resource_id || "").slice(0, 8)}</td>
                <td className="p-2">{it.user_email || it.user_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function actionColor(a) {
  if (["BLOCK", "DELETE", "LOGOUT_ALL", "DEACTIVATE"].includes(a)) return "text-red-400 border-red-500/30";
  if (["UNBLOCK", "CREATE", "REACTIVATE"].includes(a)) return "text-agro-green border-agro-green/30";
  if (["UPDATE", "ASSIGN_ROLES"].includes(a)) return "text-agro-yellow border-agro-yellow/30";
  return "text-text-muted border-border";
}
